import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

/**
 * Hook per inizializzare e gestire Google Analytics 4
 * Traccia automaticamente le visualizzazioni di pagina quando cambia la route
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();
  const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

  // Inizializza Google Analytics una volta all'avvio dell'app
  useEffect(() => {
    if (GA_MEASUREMENT_ID) {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gaOptions: {
          anonymizeIp: true, // Anonimizza IP per privacy
        },
      });
      console.log('Google Analytics inizializzato con ID:', GA_MEASUREMENT_ID);
    } else if (import.meta.env.DEV) {
      console.log('Google Analytics disabilitato in sviluppo (VITE_GA_MEASUREMENT_ID mancante)');
    }
  }, [GA_MEASUREMENT_ID]);

  // Traccia cambio di pagina quando cambia location
  useEffect(() => {
    if (GA_MEASUREMENT_ID) {
      const path = location.pathname + location.search;
      ReactGA.send({ hitType: 'pageview', page: path, title: document.title });
      console.log('GA Pageview:', path);
    }
  }, [location, GA_MEASUREMENT_ID]);
};

/**
 * Funzioni helper per tracciare eventi personalizzati
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (GA_MEASUREMENT_ID) {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    console.log('GA Event:', { category, action, label, value });
  }
};

/**
 * Traccia click su progetti
 */
export const trackProjectClick = (projectName: string, projectUrl: string) => {
  trackEvent('Projects', 'Click', projectName);
};

/**
 * Traccia apertura di link esterni
 */
export const trackExternalLink = (url: string, linkName?: string) => {
  trackEvent('External Links', 'Click', linkName || url);
};

/**
 * Traccia invio form di contatto
 */
export const trackContactFormSubmit = () => {
  trackEvent('Contact', 'Form Submit', 'Contact Form');
};

/**
 * Traccia cambio tema (dark/light)
 */
export const trackThemeChange = (theme: 'light' | 'dark') => {
  trackEvent('Theme', 'Change', theme);
};
